import {
  Alert,
  AlertIcon,
  Box,
  CloseButton,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import CommonInput from 'components/commonInput/CommonInput';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import GoogleAddressAutocomplete from 'components/googlePlaces/GoogleAddressAutocomplete';
import { useAddressGeocodeCheck } from 'hooks/useAddressGeocodeCheck';
import { useDebounce } from 'hooks/useDebounce';
import { getCountries, getProvinces, getCities } from 'localized-regions';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller } from 'react-hook-form';
import { formatPostalCode } from 'utils/PostalCode';
import { hasFieldValue } from 'utils/CommonValidation';

// RAT-1861: shared Legal/Address block, extracted out of the Merchant Legal
// section (RAT-1817/1822/1823/1824/1825/1788) so Organization and Seller can
// reuse the exact same rules instead of re-implementing them. Talks to the
// host form purely through props (control/register/watch/setValue/errors +
// a fieldNames adapter) - it never reads/writes Redux or calls an API
// directly, so swapping the data source behind it needs no change here.
//
// Not carried over: RAT-1757 (preselect city by postcode) - it isn't actually
// wired into the Merchant Legal section yet (still "Reviewable Staging" in
// Jira, no code found), so there is nothing established to replicate.

const DEFAULT_FIELD_NAMES = {
  legalName: 'legalName',
  taxId: 'NIF',
  vatNumber: 'vatNumber',
  addressLine1: 'legalAddressLine1',
  addressLine2: 'legalAddressLine2',
  zipCode: 'legalZipCode',
  country: 'legalCountry',
  province: 'legalProvince',
  city: 'legalCity',
};

// Resolve a stored name to a matching option; fall back to a synthetic option
// so existing/custom values (e.g. legacy free-text province names) still
// display and stay editable. Native-language values (opt.localized) are
// matched alongside the English name (opt.name) - RAT-1788.
const resolveLegalOption = (opts, storedName) =>
  opts.find((opt) => opt.name === storedName || opt.localized === storedName) ||
  (storedName
    ? { name: storedName, code: storedName, label: storedName }
    : null);

const LegalFieldsSection = ({
  control,
  register,
  watch,
  setValue,
  errors,
  fieldNames: fieldNamesProp,
  requiredFieldNames,
  isRequired,
  textColor,
  brandStars,
  taxIdLabel = 'Tax ID',
  locale = 'en',
  isSpainProvince = false,
  showLegalNameAndTax = true,
  initialCountryCode = '',
  initialProvinceCode = '',
  onCountryCodeChange,
  children,
}) => {
  const fieldNames = useMemo(
    () => ({ ...DEFAULT_FIELD_NAMES, ...fieldNamesProp }),
    [fieldNamesProp],
  );

  const [countryIso, setCountryIso] = useState(initialCountryCode || '');
  const [provinceIso, setProvinceIso] = useState(initialProvinceCode || '');

  // Re-seed the ISO state once, when edit-mode data arrives asynchronously
  // after the initial render (matches MerchantAddEdit's hydration-on-load).
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (hasHydrated.current) return;
    if (!initialCountryCode && !initialProvinceCode) return;
    hasHydrated.current = true;
    setCountryIso(initialCountryCode || '');
    setProvinceIso(initialProvinceCode || '');
  }, [initialCountryCode, initialProvinceCode]);

  const countryOptions = useMemo(() => getCountries({ locale }), [locale]);
  const provinceOptions = useMemo(
    () => (countryIso ? getProvinces(countryIso) : []),
    [countryIso],
  );
  const cityOptions = useMemo(
    () => (countryIso && provinceIso ? getCities(countryIso, provinceIso) : []),
    [countryIso, provinceIso],
  );

  const watchedCountry = watch(fieldNames.country);
  const watchedProvince = watch(fieldNames.province);

  // RAT-1823: every required field in the block turns required together as
  // soon as one of them holds a value.
  const requiredWatchValues = watch(requiredFieldNames);
  const isSectionRequired =
    isRequired ?? requiredWatchValues.some(hasFieldValue);

  // RAT-1825: real-time "this doesn't look like a known address" warning.
  const watchedAddressLine1 = watch(fieldNames.addressLine1);
  const watchedZipCode = watch(fieldNames.zipCode);
  const watchedCity = watch(fieldNames.city);
  const { checkAddressMatch } = useAddressGeocodeCheck();
  const [showAddressWarning, setShowAddressWarning] = useState(false);
  const [isAddressWarningDismissed, setIsAddressWarningDismissed] =
    useState(false);
  const lastCheckedAddressRef = useRef(null);
  const addressCandidate = useMemo(() => {
    const parts = [
      watchedAddressLine1,
      watchedZipCode,
      watchedCity,
      watchedCountry,
    ];
    return parts.every(hasFieldValue)
      ? `${watchedAddressLine1}, ${watchedZipCode} ${watchedCity}, ${watchedCountry}`
      : null;
  }, [watchedAddressLine1, watchedZipCode, watchedCity, watchedCountry]);
  const debouncedAddressCandidate = useDebounce(addressCandidate, 600);

  useEffect(() => {
    setIsAddressWarningDismissed(false);

    if (!debouncedAddressCandidate) {
      setShowAddressWarning(false);
      lastCheckedAddressRef.current = null;
      return;
    }

    if (lastCheckedAddressRef.current === debouncedAddressCandidate) {
      return;
    }
    lastCheckedAddressRef.current = debouncedAddressCandidate;

    let isCurrent = true;
    checkAddressMatch(debouncedAddressCandidate).then(({ matched }) => {
      if (isCurrent) {
        setShowAddressWarning(!matched);
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      isCurrent = false;
    };
  }, [debouncedAddressCandidate, checkAddressMatch]);

  // RAT-1824: "Search full address" autofill, matched against the same
  // localized-regions lists backing the cascading dropdowns below.
  const handleAddressSelected = useCallback(
    ({ line1, line2, city, stateLong, postalCode, country, businessName }) => {
      if (showLegalNameAndTax && businessName && !watch(fieldNames.legalName)) {
        setValue(fieldNames.legalName, businessName, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      if (line1) {
        setValue(fieldNames.addressLine1, line1, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (line2) {
        setValue(fieldNames.addressLine2, line2, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      const countryMatch = country
        ? countryOptions.find((opt) => opt.code === country)
        : null;
      const matchedCountryIso = countryMatch?.code || '';
      if (country) {
        setValue(
          fieldNames.country,
          countryMatch?.localized || countryMatch?.name || country,
          { shouldValidate: true, shouldDirty: true },
        );
        setCountryIso(matchedCountryIso);
        onCountryCodeChange?.(matchedCountryIso);
      }

      let matchedProvinceIso = '';
      if (stateLong) {
        const provinces = matchedCountryIso
          ? getProvinces(matchedCountryIso)
          : [];
        const provinceMatch = provinces.find(
          (opt) => opt.name.toLowerCase() === stateLong.toLowerCase(),
        );
        matchedProvinceIso = provinceMatch?.code || '';
        setValue(fieldNames.province, provinceMatch?.name || stateLong, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setProvinceIso(matchedProvinceIso);
      }

      if (city) {
        const cities =
          matchedCountryIso && matchedProvinceIso
            ? getCities(matchedCountryIso, matchedProvinceIso)
            : [];
        const cityMatch = cities.find(
          (opt) => opt.name.toLowerCase() === city.toLowerCase(),
        );
        setValue(fieldNames.city, cityMatch?.name || city, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      if (postalCode) {
        setValue(
          fieldNames.zipCode,
          formatPostalCode(postalCode, countryMatch?.name || country),
          { shouldValidate: true, shouldDirty: true },
        );
      }
    },
    [
      countryOptions,
      setValue,
      watch,
      fieldNames,
      showLegalNameAndTax,
      onCountryCodeChange,
    ],
  );

  return (
    <Box>
      <Box mb={6}>
        <GoogleAddressAutocomplete
          labelText="Search full address"
          placeholder="Start typing a street, city or zip..."
          textColor={textColor}
          onAddressSelected={handleAddressSelected}
        />
        <Text fontSize="sm" color="gray.500" ms="4px" mt="6px">
          Picking a suggestion auto-fills address, zip code, country, province
          and city.
        </Text>
      </Box>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
        {showLegalNameAndTax && (
          <CommonInput
            type="text"
            name={fieldNames.legalName}
            labelText="Legal Name"
            textColor={textColor}
            brandStars={brandStars}
            placeholder="Enter Legal Name"
            register={register(fieldNames.legalName)}
            errors={errors?.[fieldNames.legalName]}
            isValidate={isSectionRequired}
          />
        )}

        {children}

        {showLegalNameAndTax && (
          <CommonInput
            type="text"
            name={fieldNames.taxId}
            labelText={taxIdLabel}
            textColor={textColor}
            brandStars={brandStars}
            placeholder="Business Number"
            register={register(fieldNames.taxId)}
            errors={errors?.[fieldNames.taxId]}
            isValidate={isSectionRequired}
          />
        )}

        {showLegalNameAndTax && (
          <CommonInput
            type="text"
            name={fieldNames.vatNumber}
            labelText="VAT Number"
            textColor={textColor}
            brandStars={brandStars}
            placeholder="VAT Number"
            register={register(fieldNames.vatNumber)}
            errors={errors?.[fieldNames.vatNumber]}
            isValidate={isSectionRequired}
          />
        )}

        <CommonInput
          type="text"
          name={fieldNames.addressLine1}
          labelText="Address Line 1"
          textColor={textColor}
          brandStars={brandStars}
          placeholder="Enter Address Line 1"
          register={register(fieldNames.addressLine1)}
          errors={errors?.[fieldNames.addressLine1]}
          isValidate={isSectionRequired}
        />

        <CommonInput
          type="text"
          name={fieldNames.addressLine2}
          labelText="Address Line 2 (Optional)"
          textColor={textColor}
          brandStars={brandStars}
          placeholder="Enter Address Line 2"
          register={register(fieldNames.addressLine2)}
          errors={errors?.[fieldNames.addressLine2]}
          isValidate={false}
        />

        <CommonInput
          type="text"
          name={fieldNames.zipCode}
          labelText="Zip Code"
          textColor={textColor}
          brandStars={brandStars}
          placeholder="Enter Zip Code"
          register={register(fieldNames.zipCode)}
          errors={errors?.[fieldNames.zipCode]}
          isValidate={isSectionRequired}
          onBlur={(e) =>
            setValue(
              fieldNames.zipCode,
              formatPostalCode(e.target.value, watch(fieldNames.country)),
              { shouldValidate: true },
            )
          }
        />

        <Controller
          name={fieldNames.country}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CommonReactSelect
              label="Country"
              name={fieldNames.country}
              type="select"
              creatable
              options={countryOptions}
              value={resolveLegalOption(countryOptions, value)}
              onChange={(option) => {
                onChange(option?.localized || option?.name || '');
                const nextCode = option?.code || '';
                setCountryIso(nextCode);
                onCountryCodeChange?.(nextCode);
                setProvinceIso('');
                setValue(fieldNames.province, '');
                setValue(fieldNames.city, '');
              }}
              getNewOptionData={(input) => ({
                code: input,
                name: input,
                label: input,
              })}
              register={register}
              isRequired={isSectionRequired}
              error={errors?.[fieldNames.country]}
              getOptionLabel={(e) => e?.label || e?.name}
              getOptionValue={(e) => e?.code}
            />
          )}
        />

        <Controller
          name={fieldNames.province}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CommonReactSelect
              label="Province"
              labelExtra={
                !isSpainProvince && (
                  <Box as="span" fontSize="xs" color="gray.500">
                    (Won&apos;t display on invoice)
                  </Box>
                )
              }
              name={fieldNames.province}
              type="select"
              creatable
              options={provinceOptions}
              value={resolveLegalOption(provinceOptions, value)}
              onChange={(option) => {
                onChange(option?.name || '');
                setProvinceIso(option?.code || '');
                setValue(fieldNames.city, '');
              }}
              getNewOptionData={(input) => ({ code: input, name: input })}
              register={register}
              isRequired={isSectionRequired}
              error={errors?.[fieldNames.province]}
              getOptionLabel={(e) => e?.name}
              getOptionValue={(e) => e?.code}
              isDisabled={!watchedCountry}
            />
          )}
        />

        <Controller
          name={fieldNames.city}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CommonReactSelect
              label="City"
              name={fieldNames.city}
              type="select"
              creatable
              options={cityOptions}
              value={resolveLegalOption(cityOptions, value)}
              onChange={(option) => onChange(option?.name || '')}
              getNewOptionData={(input) => ({ name: input })}
              register={register}
              isRequired={isSectionRequired}
              error={errors?.[fieldNames.city]}
              getOptionLabel={(e) => e?.name}
              getOptionValue={(e) => e?.name}
              isDisabled={!watchedProvince}
            />
          )}
        />
      </SimpleGrid>
      {showAddressWarning && !isAddressWarningDismissed && (
        <Alert status="warning" borderRadius="md" mt={4}>
          <AlertIcon />
          <Box flex="1">
            This address may contain a typo - we couldn&apos;t find it in our
            address database. Please double-check it. If you&apos;re confident
            it&apos;s correct, you can ignore this warning.
          </Box>
          <CloseButton onClick={() => setIsAddressWarningDismissed(true)} />
        </Alert>
      )}
    </Box>
  );
};

LegalFieldsSection.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object,
  fieldNames: PropTypes.object,
  requiredFieldNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  isRequired: PropTypes.bool,
  textColor: PropTypes.string,
  brandStars: PropTypes.string,
  taxIdLabel: PropTypes.string,
  locale: PropTypes.string,
  isSpainProvince: PropTypes.bool,
  showLegalNameAndTax: PropTypes.bool,
  initialCountryCode: PropTypes.string,
  initialProvinceCode: PropTypes.string,
  onCountryCodeChange: PropTypes.func,
  children: PropTypes.node,
};

export default LegalFieldsSection;
